import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { affiliationAPI } from '../../services/api';
import { Loader2, ShieldCheck, CalendarClock, Receipt, RefreshCw } from 'lucide-react';

const ETIQUETA = {
  vigente: { texto: 'Vigente', clase: 'bg-emerald-100 text-emerald-700' },
  por_vencer: { texto: 'Por vencer', clase: 'bg-amber-100 text-amber-700' },
  vencido: { texto: 'Vencida', clase: 'bg-red-100 text-red-700' },
  sin_vigencia: { texto: 'Sin vigencia registrada', clase: 'bg-gray-100 text-gray-700' },
};

const fecha = (v) => (v ? new Date(v).toLocaleDateString('es-MX', {
  day: 'numeric', month: 'long', year: 'numeric',
}) : '—');

export const MiAfiliacionCard = () => {
  const { toast } = useToast();
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [pagando, setPagando] = useState(false);
  const [sinAfiliacion, setSinAfiliacion] = useState(false);
  const [comprobante, setComprobante] = useState(false);

  useEffect(() => {
    affiliationAPI.miAfiliacion()
      .then(setDatos)
      .catch(() => setSinAfiliacion(true))
      .finally(() => setCargando(false));
  }, []);

  const renovar = async () => {
    setPagando(true);
    try {
      const r = await affiliationAPI.miRenovacionCheckout();
      window.location.href = r.checkout_url;
    } catch (e) {
      toast({ title: 'No se pudo iniciar la renovación', description: e.message, variant: 'destructive' });
      setPagando(false);
    }
  };

  if (cargando) {
    return (
      <Card data-testid="mi-afiliacion-cargando">
        <CardContent className="p-6 flex items-center text-gray-500">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Consultando tu afiliación…
        </CardContent>
      </Card>
    );
  }
  if (sinAfiliacion || !datos) {
    return (
      <Card data-testid="mi-afiliacion-vacia">
        <CardContent className="p-6 text-sm text-gray-600">
          No encontramos una afiliación asociada a tu cuenta.
        </CardContent>
      </Card>
    );
  }

  const est = ETIQUETA[datos.vigencia_estado] || ETIQUETA.sin_vigencia;
  return (
    <Card data-testid="mi-afiliacion-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center">
              <ShieldCheck className="h-5 w-5 mr-2 text-emerald-600" />
              Mi afiliación
            </CardTitle>
            <CardDescription>Información oficial de tu expediente UPIM</CardDescription>
          </div>
          <Badge className={est.clase} data-testid="mi-afiliacion-estado-vigencia">{est.texto}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <div>
            <p className="text-xs text-gray-500">Folio</p>
            <p className="font-semibold" data-testid="mi-afiliacion-folio">{datos.folio}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Sección</p>
            <p className="font-semibold" data-testid="mi-afiliacion-seccion">{datos.seccion}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Estado del trámite</p>
            <p className="font-semibold" data-testid="mi-afiliacion-estado">{datos.estado}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Vigencia hasta</p>
            <p className="font-semibold" data-testid="mi-afiliacion-vigencia-hasta">
              {fecha(datos.vigencia_hasta)}
            </p>
          </div>
        </div>

        <div className="flex items-center text-sm text-gray-600" data-testid="mi-afiliacion-dias">
          <CalendarClock className="h-4 w-4 mr-2 text-gray-400" />
          {datos.dias_para_vencer === null || datos.dias_para_vencer === undefined
            ? 'Aún no tienes una vigencia registrada.'
            : datos.dias_para_vencer < 0
              ? `Venció hace ${Math.abs(datos.dias_para_vencer)} días.`
              : `Te quedan ${datos.dias_para_vencer} días de vigencia.`}
        </div>

        <div className="flex flex-wrap gap-3">
          {datos.puede_renovar && (
            <Button onClick={renovar} disabled={pagando} data-testid="mi-afiliacion-renovar-btn"
                    className="bg-emerald-600 hover:bg-emerald-700">
              {pagando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                       : <RefreshCw className="h-4 w-4 mr-2" />}
              Renovar ${datos.cuota_anual?.toLocaleString('es-MX')} {datos.moneda}
            </Button>
          )}
          {datos.comprobante_disponible && (
            <Button variant="outline" onClick={() => setComprobante((v) => !v)}
                    data-testid="mi-afiliacion-comprobante-btn">
              <Receipt className="h-4 w-4 mr-2" />
              {comprobante ? 'Ocultar comprobante' : 'Ver comprobante'}
            </Button>
          )}
        </div>

        {comprobante && datos.comprobante && (
          <div className="rounded-lg border bg-gray-50 p-4 text-sm space-y-1"
               data-testid="mi-afiliacion-comprobante">
            <p className="font-semibold">{datos.comprobante.concepto}</p>
            <p>Folio: {datos.comprobante.folio}</p>
            <p>Importe: ${datos.comprobante.importe?.toLocaleString('es-MX')} {datos.comprobante.moneda}</p>
            <p>Sección: {datos.comprobante.seccion}</p>
            <p>Fecha de pago: {fecha(datos.comprobante.fecha_pago)}</p>
            <p className="text-xs text-gray-500">{datos.comprobante.sello}</p>
          </div>
        )}

        <p className="text-xs text-gray-500" data-testid="mi-afiliacion-aviso">
          Modo de cobro {datos.modo_pago}. {datos.aviso}
        </p>
      </CardContent>
    </Card>
  );
};

export default MiAfiliacionCard;
